import { cx } from '@linaria/core';
import { memo, useState, useEffect, Suspense } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { pm2CurrentAST } from '@pm2/content-engine';
import { pm2SitemapStore, findPageDescriptorByPath } from '@pm2/sitemap-utils';
import { pm2ConfigStore } from '@pm2/store';
import { useLocation } from '@provider/navigation';
import { useUserIdentityState } from '@provider/user-identity';
import { breakpoints } from '@ui/css';
import { SpeechButton } from '@ui/kit';
import { useObservedValue, useMatchBreakpoint, NoSSR, CSSRefTransition, SizeTransition, Chevron } from '@utils/react';
import { transitionDurationMs, initialTransition, mobileCollapseTransition, mobileToggleDurationMs, mobileContainer, mobileToggle, floatingPanel } from './_floating-buttons.style.js';
import { ABCOrdbogenButton } from './abc-ordbogen/_abc-ordbogen.js';
import { PrintButton } from './print/_print.js';
import { floatingButtonSelectors } from './selectors.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { SubNavigationButton } from './sub-navigation/sub-navigation.js';
import { NotebookButton } from './notebook/notebook-button.js';
import { Toolbox } from './toolbox/root.toolbox.js';

function FloatingButtonsNonMemoized() {
  const user = useUserIdentityState(state => state.user);
  const location = useLocation();
  const sitemap = pm2SitemapStore.useSelector(state => state);
  const page = findPageDescriptorByPath(sitemap, location.pathname);
  const currentAST = useObservedValue(pm2CurrentAST);
  const [mobileToggled, setMobileToggled] = useState(false);
  const isMobile = useMatchBreakpoint(breakpoints.mobile);
  const isLibraryPage = page?.type === "Library";
  const isBookshelfPage = page?.type === "Shelf";
  const isSiteSearchPage = page?.type === "SiteSearch";
  const allowNoteCreation = !isLibraryPage && !isBookshelfPage && !isSiteSearchPage;
  const hideOrdbogenButton = pm2ConfigStore.useSelector(state => !state.config.settings.ordbogenDotComEnabled);
  useEffect(() => {
    document.documentElement.classList.toggle("fab-hidden", !user);
  }, [user]);
  if (user === undefined) {
    return null;
  }
  return /*#__PURE__*/jsx(NoSSR, {
    children: /*#__PURE__*/jsx(Suspense, {
      fallback: null,
      children: /*#__PURE__*/jsxs(TransitionGroup, {
        component: null,
        appear: true,
        children: [/*#__PURE__*/jsx(CSSRefTransition, {
          classNames: initialTransition,
          timeout: transitionDurationMs,
          children: ref => /*#__PURE__*/jsx(SubNavigationButton, {
            ref: ref,
            className: initialTransition
          })
        }), !!user && /*#__PURE__*/jsx(CSSRefTransition, {
          classNames: initialTransition,
          timeout: transitionDurationMs,
          children: ref => /*#__PURE__*/jsxs("div", {
            ref: ref,
            className: cx(floatingPanel, initialTransition, !hideOrdbogenButton && "withOrdbogen"),
            children: [/*#__PURE__*/jsx(SpeechButton, {
              wrapperClassName: floatingButtonSelectors.speechSynthesis.className,
              flagPlacement: isMobile ? "left" : "bottom",
              enableKeyboardShortcut: true
            }), /*#__PURE__*/jsx(SizeTransition, {
              in: !isMobile || mobileToggled,
              timeout: mobileToggleDurationMs,
              axis: "horisontal",
              classNames: mobileCollapseTransition,
              mountOnEnter: true,
              unmountOnExit: true,
              children: ref => /*#__PURE__*/jsx("div", {
                ref: ref,
                className: mobileCollapseTransition,
                children: /*#__PURE__*/jsxs("div", {
                  className: mobileContainer,
                  children: [/*#__PURE__*/jsx(NotebookButton, {
                    wrapperClassName: floatingButtonSelectors.notebook.className,
                    allowNoteCreation: allowNoteCreation
                  }), /*#__PURE__*/jsx(PrintButton, {
                    currentAST: currentAST
                  }), !hideOrdbogenButton && /*#__PURE__*/jsx(ABCOrdbogenButton, {}), /*#__PURE__*/jsx(Toolbox, {})]
                })
              })
            }), /*#__PURE__*/jsx("div", {
              children: /*#__PURE__*/jsx("button", {
                className: mobileToggle,
                onClick: () => setMobileToggled(prevValue => !prevValue),
                children: /*#__PURE__*/jsx(Chevron, {
                  inversed: mobileToggled,
                  lineWidth: 1.5,
                  size: 8
                })
              })
            })]
          })
        })]
      })
    })
  });
}
const FloatingButtons = /*#__PURE__*/memo(FloatingButtonsNonMemoized);

export { FloatingButtons };
//# sourceMappingURL=_floating-buttons.js.map
